import styled from "@emotion/styled";
import { Accordion, AllCaps, H2, Link, List, Stack, Text } from "atoms";
import { constants } from "config";
import { paths } from "utils/paths";

function OpenCallFAQs({
  isAfterSubmissionWindow,
}: {
  isAfterSubmissionWindow?: boolean;
}) {
  return (
    <FAQsWrapper>
      <H2 size="md">Open Call FAQs</H2>

      <SAccordion heading="How are artists selected?">
        <Stack gap="sm">
          <SText>
            Depending on the Open Call and its respective partner, artists can
            be selected either at the sole discretion of our partner or with
            input from our community.
          </SText>
          <Stack gap="xxs">
            <SText bold>About Community Voting</SText>
            <SText>
              We invite our community of art lovers to participate in the
              curation process by allowing them to vote for their favorite
              artworks in each Open Call. This also allows artists to get their
              work in front of more people who may or may not already be
              familiar with them.
            </SText>
            <SText>
              Our partners receive the entire list of submissions for review and
              selection, and have the list of community favorites available to
              them in case they would like to factor that into their decision.
            </SText>

            <SText>
              Artists can invite anyone to create a free <AllCaps>Hug</AllCaps>{" "}
              account and vote for their artwork to be highlighted to our
              partners. Each vote submitted by a Curator or Senior Curator is
              counted double and triple respectively.
            </SText>
          </Stack>
        </Stack>
      </SAccordion>

      <SAccordion heading="Who is eligible to submit artwork? ">
        <SText>
          Everyone is welcome to submit their artwork and have it discovered and
          celebrated by our community through the respective Open Call page.
          However, only artists with a published or pending{" "}
          <AllCaps>Hug</AllCaps> Artist Profile are eligible to be selected.{" "}
        </SText>
        <SText>
          <Link to={paths.artistProfileBenefits()}>
            Learn more and apply for a free <AllCaps>Hug</AllCaps> Artist
            Profile today.
          </Link>
        </SText>
      </SAccordion>
      {!isAfterSubmissionWindow && (
        <>
          <SAccordion heading="How many Open Calls can I enter?">
            <SText>
              There is no limit to the number of Open Calls you can enter at any
              one time, so long as your submission meets the requirements for
              the respective Open Call.
            </SText>
          </SAccordion>

          <SAccordion heading="How many times can I submit to each Open Call?">
            <SText>
              Each artist can submit one free entry per open call. For some open
              calls, artists can purchase two additional submissions for $10.
              These additional entries are non-refundable, and do not guarantee
              selection nor multiple placements.
            </SText>
          </SAccordion>

          <SAccordion heading="Can I change my submission to an Open Call?">
            <SText>
              You are able to edit your submission for as long as submissions
              are being accepted. You can see all of your Open Call submissions
              on your <Link to="/profile#submissions">Profile</Link> page.
              Entries cannot be modified once an Open Call submission period has
              ended.
            </SText>
          </SAccordion>
        </>
      )}

      <SAccordion heading="Who is eligible to vote on submissions?">
        <SText>
          If an Open Call involves community voting, anyone with a free{" "}
          <AllCaps>Hug</AllCaps> account can vote. <AllCaps>Hug</AllCaps>{" "}
          Curators and Senior Curators will have their vote count double and
          triple respectively.
        </SText>
        <SText>
          Community voting is used to highlight community favorites to our
          partners, but will never be the only input used in determining which
          artists get selected.
        </SText>
        <SText>
          Become an official <AllCaps>Hug</AllCaps> Curator and have your vote
          multiplied by purchasing a{" "}
          <Link href={constants.openseaURL} target="_blank">
            <AllCaps>Hug</AllCaps> Pass
          </Link>{" "}
          or{" "}
          <Link href={constants.openseaMetaAngels} target="_blank">
            Meta Angels Membership
          </Link>
          .
        </SText>
      </SAccordion>
      {!isAfterSubmissionWindow && (
        <>
          <SAccordion heading="When will I be notified of results?">
            <SText>
              Timelines for submission and curation vary with each Open Call
              opportunity. As soon as curation for a given opportunity is
              completed, selected artists will be notified via the email
              associated with their <AllCaps>Hug</AllCaps> account.
            </SText>
          </SAccordion>

          <SAccordion heading="What are the next steps after I submit my artwork?">
            <SText>
              While you wait to be notified of a decision, we welcome you to
              explore all of <AllCaps>Hug</AllCaps>&apos;s offerings to take you
              further on your journey as an artist and an art lover.{" "}
            </SText>
            <List>
              <li>
                <SText>
                  Personalize and edit your own <AllCaps>Hug</AllCaps> Artist
                  Profile
                </SText>
              </li>
              <li>
                <SText>
                  <Link to="/open-calls">
                    Submit your artwork to more Open Calls
                  </Link>
                </SText>
              </li>
              <li>
                <SText>
                  <Link to="/resources">
                    Download free educational tools, guides, and resources
                  </Link>
                </SText>
              </li>
              <li>
                <SText>
                  <Link to="/search?type=artist&sort=reviewCount+desc">
                    Discovers artists by creative mediums and identity
                    attributes
                  </Link>
                </SText>
              </li>
              <li>
                <SText>
                  <Link to="/artist-updates">
                    Browse the latest art drops and announcements on Artist
                    Updates
                  </Link>
                </SText>
              </li>
            </List>
          </SAccordion>
        </>
      )}
    </FAQsWrapper>
  );
}
const SText = styled(Text)({});
SText.defaultProps = {
  size: "xs",
};

const FAQsWrapper = styled.div({
  marginTop: "4rem",
});

const SAccordion = styled(Accordion)(({ theme }) => ({
  paddingBottom: 0,
  borderColor: theme.colors.fg20,
  summary: {
    padding: "10px 0",
    fontSize: theme.fontSizes.sm,
  },
}));

export { OpenCallFAQs };
